/* @refresh reload */
import "./index.css";
import { render } from "solid-js/web";
import App from "./App";
import { ErrorBoundary } from "solid-js";
import * as Sentry from "@sentry/solid";
import { replayIntegration, withSentryErrorBoundary } from "@sentry/solid";
import { solidRouterBrowserTracingIntegration } from "@sentry/solid/solidrouter";
import { AuthProvider } from "./state/auth";
import { ClientProvider } from "./state/client";
import { I18nProvider } from "./state/i18n";
import posthog from "posthog-js";
import QueryClientProvider from "./QueryClientProvider";
import { MetaProvider } from "@solidjs/meta";
import GlobalErrorPage from "./pages/GlobalErrorPage";
import { removeSensitiveParams } from "./utils/posthogUtil";
import { MoreAppError } from "./MoreAppError";
import environment from "./environment";
import { HubSpotProvider } from "./hubSpot";

if (environment.isLive()) {
  Sentry.init({
    environment: environment.deployment,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: import.meta.env.VITE_COMMIT_SHA,
    integrations: [
      solidRouterBrowserTracingIntegration(),
      replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0, // Set to 1.0 to receive replays during development
    normalizeDepth: 5,
    beforeSend: (event, hint) => {
      // Do not send API permission errors to Sentry
      if (isApiPermissionError(hint.originalException)) {
        return null;
      }

      return event;
    },
    beforeSendLog: (log) => {
      log.attributes = {
        ...log.attributes,
        release: import.meta.env.VITE_VERSION,
      };
      return log;
    },
  });
}

const isApiPermissionError = (exception: unknown) => exception instanceof MoreAppError && exception.status === 403;

posthog.init(import.meta.env.VITE_POST_HOG_TOKEN, {
  api_host: import.meta.env.VITE_POST_HOG_API_HOST,
  ui_host: import.meta.env.VITE_POST_HOG_UI_HOST,
  autocapture: false,
  loaded: (ph) => {
    ph.setPersonProperties({ environment: environment.isProduction() ? "production" : "development" });

    // Disable all event capturing and session recording when not on a 'live' environment
    if (!environment.isLive()) {
      ph.opt_out_capturing(); // opts a user out of event capture
      ph.set_config({ disable_session_recording: true });
    }
  },
  before_send: removeSensitiveParams,
});

// recommended by Vite to fix import module errors
// https://vite.dev/guide/build#load-error-handling
globalThis.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  globalThis.location.reload();
});

const root = document.getElementById("root");

if (!(root instanceof HTMLElement)) {
  throw new Error("Root element not found");
}

const SentryErrorBoundary = withSentryErrorBoundary(ErrorBoundary);

render(
  () => (
    <SentryErrorBoundary fallback={(err) => <pre>{err.stack}</pre>}>
      <I18nProvider value={undefined}>
        <MetaProvider>
          <SentryErrorBoundary fallback={(err) => <GlobalErrorPage err={err} />}>
            <AuthProvider value={undefined}>
              <QueryClientProvider>
                <ClientProvider value={undefined}>
                  <HubSpotProvider value={undefined}>
                    <App />
                  </HubSpotProvider>
                </ClientProvider>
              </QueryClientProvider>
            </AuthProvider>
          </SentryErrorBoundary>
        </MetaProvider>
      </I18nProvider>
    </SentryErrorBoundary>
  ),
  root,
);
